import React, { useState } from 'react'

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Collapse from 'components_new/atoms/Collapse'
import Drawer from 'components_new/atoms/Drawer'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import LoadingSpinner from 'components_new/atoms/LoadingSpinner'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import { useTranslation } from 'language/translator'
import {
  SimChat,
  SimChatMessage
} from 'redux/selectors/internal/SimChats'

import { getParsedDate } from 'utils/dateParser'

const PANEL_WIDTH = 560

const RATING_LABEL: Record<'up' | 'down', string> = {
  up: 'Bra svar',
  down: 'Dåligt svar'
}

function boardLabel(message: SimChatMessage): string | null {
  const { board } = message

  if (!board) return null

  const { proposals_accepted: accepted, proposals_rejected: rejected } = board

  if (accepted === 0 && rejected === 0) return null

  if (accepted === 0) return `Tavlan oförändrad · ${rejected} avvisade`

  return rejected > 0
    ? `Tavlan ändrad · ${accepted} accepterade · ${rejected} avvisade`
    : `Tavlan ändrad · ${accepted} accepterade`
}

function formatDuration(ms: number | null): string | null {
  if (ms === null) return null

  return ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`
}

/** A section the reader opens on demand: tool calls, reasoning, boards. */
const Foldable = (props: { title: string; children: React.ReactNode }) => {
  const { title, children } = props

  const [open, setOpen] = useState(false)

  return (
    <Box sx={{ mt: 0.5 }}>
      <Button
        onClick={() => setOpen((value) => !value)}
        size="small"
        startIcon={
          <Icon fontSize="small" name={open ? 'ExpandLess' : 'ExpandMore'} />
        }
        variant="text"
      >
        {title}
      </Button>
      <Collapse in={open}>
        <Box sx={{ pb: 1 }}>{children}</Box>
      </Collapse>
    </Box>
  )
}

const Pre = (props: { value: unknown }) => (
  <Box
    component="pre"
    sx={{
      bgcolor: 'action.hover',
      borderRadius: 1,
      fontSize: '0.75rem',
      m: 0,
      maxHeight: 320,
      overflow: 'auto',
      p: 1,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word'
    }}
  >
    {typeof props.value === 'string'
      ? props.value
      : JSON.stringify(props.value, null, 2)}
  </Box>
)

const UserMessage = (props: { message: SimChatMessage }) => {
  const { message } = props

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', mb: 2 }}>
      <Box sx={{ alignSelf: 'flex-end', maxWidth: '85%' }}>
        <Box
          sx={{
            bgcolor: 'action.selected',
            borderRadius: 2,
            px: 1.5,
            py: 1
          }}
        >
          <Text variant="body2">{message.content}</Text>
        </Box>
      </Box>
      {message.nodes ? (
        <Box sx={{ alignSelf: 'flex-end' }}>
          <Foldable title={`Tavlan vid frågan (${message.nodes.length} noder)`}>
            <Pre value={message.nodes} />
          </Foldable>
        </Box>
      ) : null}
    </Box>
  )
}

const AssistantMessage = (props: { message: SimChatMessage }) => {
  const { message } = props

  const board = boardLabel(message)
  const duration = formatDuration(message.duration_ms)
  const steps = message.thinking_steps ?? []

  return (
    <Box sx={{ mb: 2, '& p': { my: 0.5 }, fontSize: '0.875rem' }}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {message.content || '_(tomt svar)_'}
      </ReactMarkdown>

      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mt: 0.5
        }}
      >
        {board ? (
          <Text color="text.secondary" variant="caption">
            {board}
          </Text>
        ) : null}
        {duration ? (
          <Text color="text.secondary" variant="caption">
            {duration}
          </Text>
        ) : null}
        {message.input_tokens !== null ? (
          <Text color="text.secondary" variant="caption">
            {`${message.input_tokens} in / ${message.output_tokens ?? 0} ut`}
          </Text>
        ) : null}
        {message.model ? (
          <Text color="text.secondary" variant="caption">
            {message.model}
          </Text>
        ) : null}
      </Box>

      {message.rating ? (
        <Box sx={{ mt: 1 }}>
          <Tag
            color={message.rating === 'up' ? 'success' : 'error'}
            label={
              message.rating_reason
                ? `${RATING_LABEL[message.rating]} · ${message.rating_reason}`
                : RATING_LABEL[message.rating]
            }
          />
          {message.rating_comment ? (
            <Text sx={{ mt: 0.5 }} variant="body2">
              {message.rating_comment}
            </Text>
          ) : null}
        </Box>
      ) : null}

      {steps.length > 0 ? (
        <Foldable title={`Verktygsanrop (${steps.length})`}>
          {steps.map((step, index) => (
            <Box key={index} sx={{ mb: 1 }}>
              <Text fontWeight="700" variant="caption">
                {step.tool}
                {step.duration_ms !== undefined
                  ? ` · ${formatDuration(step.duration_ms)}`
                  : ''}
              </Text>
              <Pre value={step.input} />
            </Box>
          ))}
        </Foldable>
      ) : null}

      {message.reasoning_text ? (
        <Foldable title="Resonemang">
          <Pre value={message.reasoning_text} />
        </Foldable>
      ) : null}

      {message.nodes_after ? (
        <Foldable title={`Tavlan efter svaret (${message.nodes_after.length} noder)`}>
          <Pre value={message.nodes_after} />
        </Foldable>
      ) : null}
    </Box>
  )
}

interface TranscriptPanelProps {
  chat: SimChat | null
  loading: boolean
  onClose: () => void
  open: boolean
}

/**
 * One stored SIM chat, turn by turn, with what the reader needs to judge a
 * reply: the board each message was sent with, the tools the reply ran, its
 * reasoning, what it did to the board and how the user rated it.
 */
const TranscriptPanel = (props: TranscriptPanelProps) => {
  const {
    chat, loading, onClose, open
  } = props

  const { lang } = useTranslation()

  const [copied, setCopied] = useState(false)

  // The whole chat as JSON, for replaying a turn outside the portal.
  const copyJson = () => {
    if (!chat) return

    navigator.clipboard.writeText(JSON.stringify(chat, null, 2)).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Drawer
      anchor="right"
      onClose={onClose}
      open={open}
      PaperProps={{
        sx: { borderLeft: '1px solid', borderColor: 'divider' }
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          width: PANEL_WIDTH
        }}
      >
        <Box
          sx={{
            alignItems: 'flex-start',
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            gap: 1,
            justifyContent: 'space-between',
            px: 2,
            py: 1.5
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Text fontWeight="700" noWrap={true} variant="subtitle1">
              {chat?.title ?? 'Chatt'}
            </Text>
            {chat ? (
              <Text color="text.secondary" variant="caption">
                {[
                  chat.customer_name ?? chat.customer_id,
                  chat.table_name ?? chat.table_id,
                  chat.account_email ?? chat.account_id,
                  getParsedDate(chat.created_at, lang, { time: 'full' })
                ].join(' · ')}
              </Text>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', flexShrink: 0, gap: 0.5 }}>
            <Tooltip title={copied ? 'Kopierat' : 'Kopiera chatten som JSON'}>
              <span>
                <IconButton disabled={!chat} onClick={copyJson} size="small">
                  <Icon fontSize="small" name="Copy" />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton onClick={onClose} size="small">
              <Icon fontSize="small" name="Close" />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 1.5 }}>
          {loading && !chat ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <LoadingSpinner size={24} />
            </Box>
          ) : null}

          {chat?.messages.map((message) =>
            message.role === 'user' ? (
              <UserMessage key={message.id} message={message} />
            ) : (
              <AssistantMessage key={message.id} message={message} />
            )
          )}
        </Box>
      </Box>
    </Drawer>
  )
}

export default TranscriptPanel
